import { createModel, RematchDispatch } from '@rematch/core';
import { produce } from 'immer';

import baseEntity from './base.entity';
import { storeName as projectItemEntityStore } from './projectItem.entity';
import { storeName as newsEntityStore } from './news.entity';
import { RootModel } from '../models';
import { storeName as layoutModelStore } from '../models/layout.model';
import { ProjectTypes } from '../../types';
import { PROJECT_CONFIG_KEY } from '../../commons/constants';
import {
  authorizeProject,
  getProjectByKey,
  getReviewsByCode,
} from '../../services/project.service';
import { convertProjectResponse } from '../../utils/convertUtils/project';
import { ProjectResponseTypes } from '../../types/response';

export const storeName = 'projectEntity';

export const projectEntity = createModel<RootModel>()({
  name: storeName,
  state: { ids: {} },
  reducers: {
    ...baseEntity,
    saves: produce((state, entities: ProjectTypes[]) => {
      (entities || []).forEach((entity: ProjectTypes) => {
        state.ids[entity.id] = entity;
      });
    }),
  },
  effects: (dispatch: RematchDispatch<typeof projectEntity>) => ({
    async checkAuthorizeProject(
      payload: { id: number; key: string; token: string },
      state: RootModel
    ) {
      const { id, key, token } = payload;
      const { locale } = state[layoutModelStore];

      const entities: { [key: string]: ProjectTypes } = state[storeName].ids;
      const savedItem: ProjectTypes = entities[id];
      if (savedItem) return savedItem;

      const response = await authorizeProject(key, token, locale.apiLanguage);
      if (response.code) {
        const respReview = await getReviewsByCode(response.code, locale.apiLanguage);
        response.reviews = respReview;
      }

      const project = storeProject(response, dispatch, this);

      return project;
    },
    async loadByKey(payload: string, state: RootModel) {
      const { locale } = state[layoutModelStore];

      const resp = await getProjectByKey(payload, locale.apiLanguage);
      const project = storeProject(resp, dispatch, this);

      return project;
    },
  }),
});

export default projectEntity;

const storeProject = (response: any, dispatch: RematchDispatch, _this: any) => {
  if (!response) return { notFound: true };
  if (response.notFound || response.invalid) return response;

  if (response.configs && response.configs.length) {
    const loadingLogo = (response as ProjectResponseTypes).configs.find(
      (i) => i && i.type === PROJECT_CONFIG_KEY.LOADING_LOGO
    );

    if (loadingLogo) {
      dispatch[layoutModelStore].setLoadingLogo(loadingLogo.loadingLogo);
    }
  }

  const project = convertProjectResponse(response);
  const { news = [], items = [] } = project;
  dispatch[projectItemEntityStore].saveSimplified(items);
  dispatch[newsEntityStore].saveSimplified(news);
  _this.saves([project]);

  return project;
};
